import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface SocialTopicOverviewProps {
  /** The region id the stat row's toggle points at (`aria-controls`). */
  id: string;
  isOpen: boolean;
  /** Left column: the timeline panel. Its height sets the share-of-voice card. */
  main: ReactNode;
  /** Right column: share of voice, clipped to the timeline. Omitted with no brands. */
  side: ReactNode;
  /** Full width under the chart row: channels and top voices, half and half. */
  split?: ReactNode;
  /** Full width under both columns: the highlights, three across on a wide page. */
  footer?: ReactNode;
}

/**
 * Tier 2 of the Topic page: folds away under the stat row (grid rows 1fr to
 * 0fr in 420 ms with a 300 ms fade; reduced motion skips both). The timeline
 * and share of voice sit side by side from 960 px of container width (the
 * voice card is 340 to 380 px and takes the timeline's height). Channels and
 * top voices are a full-width row under that. Closed, the region stays
 * mounted but is hidden from assistive tech and the tab order.
 */
export function SocialTopicOverview(props: SocialTopicOverviewProps) {
  const { id, isOpen, main, side, split, footer } = props;

  const { t } = useTranslation("app");

  // React 18 types have no `inert`; the DOM attribute takes the tab order and
  // the accessibility tree away from the folded region.
  const inertProps: Record<string, string> = isOpen ? {} : { inert: "" };

  return (
    <section
      id={id}
      aria-label={t("socialListening.topicPage.overview.ariaLabel")}
      aria-hidden={!isOpen}
      {...inertProps}
      data-state={isOpen ? "open" : "closed"}
      data-testid="social-topic-overview"
      className={
        "grid transition-[grid-template-rows,opacity] duration-[420ms,300ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none " +
        (isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")
      }
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={
            "grid grid-cols-1 items-stretch gap-4 pb-2 " +
            (side ? "@[960px]/topic:grid-cols-[minmax(0,1fr)_clamp(340px,30%,380px)]" : "")
          }
          data-testid="social-topic-overview-grid"
        >
          <div className="flex min-w-0 flex-col gap-4" data-testid="social-topic-overview-main">
            {main}
          </div>
          {side ? (
            <div className="relative min-h-0 min-w-0" data-testid="social-topic-overview-side">
              {/* Out of flow beside the chart, so the chart alone sets the row height. */}
              <div
                className="flex h-80 min-h-0 flex-col overflow-hidden @[960px]/topic:absolute @[960px]/topic:inset-0 @[960px]/topic:h-full"
                data-testid="social-topic-overview-side-clip"
              >
                {side}
              </div>
            </div>
          ) : null}
          {split ? (
            <div className={side ? "min-w-0 @[960px]/topic:col-span-2" : "min-w-0"} data-testid="social-topic-overview-split">
              {split}
            </div>
          ) : null}
          {footer ? (
            <div className={side ? "min-w-0 @[960px]/topic:col-span-2" : "min-w-0"} data-testid="social-topic-overview-footer">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
